/**
 * Auth Datasource (Infrastructure)
 *
 * Handles raw API communication for authentication.
 * User-related APIs are in the user module.
 */

import { apiClient } from '@/shared/api';

import type { LoginRequest, LogoutRequest } from '../../domain/types/requests';
import type { AuthResponse, TokenRefreshResponse, GoogleAuthUrlResponse } from '../../domain/types/responses';

// ============================================================================
// Datasource Interface
// ============================================================================

export interface AuthDatasource {
  /**
   * Get Google OAuth URL for web login
   * Calls: GET /auth/google-login-url?platform=web
   */
  getGoogleAuthUrl(): Promise<GoogleAuthUrlResponse>;

  /**
   * Authenticate using Google authorization code (from OAuth callback)
   * Calls: POST /auth/google-login with { server_auth_code: ..., scopes: ... }
   */
  googleLoginWithAuthCode(authCode: string, scopes?: string): Promise<AuthResponse>;

  /**
   * Login with email and password
   * Calls: POST /login with { email: ..., password: ... }
   */
  login(request: LoginRequest): Promise<AuthResponse>;

  /**
   * Refresh access token using refresh token cookie
   * Calls: POST /auth/refresh (cookie sent automatically)
   */
  refreshToken(): Promise<TokenRefreshResponse>;

  /**
   * Logout user
   * Calls: POST /auth/logout
   */
  logout(request?: LogoutRequest): Promise<void>;
}

// ============================================================================
// Datasource Implementation
// ============================================================================

export class AuthDatasourceImpl implements AuthDatasource {
  async getGoogleAuthUrl(): Promise<GoogleAuthUrlResponse> {
    const response = await apiClient.get<GoogleAuthUrlResponse>('/auth/google-login-url', { params: { platform: 'web' } });
    return response.data;
  }

  async googleLoginWithAuthCode(authCode: string, scopes?: string): Promise<AuthResponse> {
    // Step 1: Exchange auth code for tokens (refresh token set as cookie by backend)
    const tokenResponse = await apiClient.post<{
      access_token: string;
      expires_in: number;
    }>('/auth/google-login', {
      server_auth_code: authCode,
      scopes: scopes,
    });

    const tokens = {
      accessToken: tokenResponse.data.access_token,
      expiresIn: tokenResponse.data.expires_in,
      tokenType: 'Bearer' as const,
    };

    // Step 2: Fetch user profile using the user module's datasource
    // We pass the new token directly to avoid any race conditions
    const userResponse = await apiClient.get<{
      authenticatedId: string;
      displayName: string;
      extension?: string;
      extension_type?: string;
      role?: string;
      photo_url?: string;
      email: string;
      mobileNumber?: string;
      status?: string;
      sip_password?: string;
    }>('/app/me', {
      headers: { Authorization: `Bearer ${tokens.accessToken}` },
    });

    // Map API response to User entity
    const user = {
      id: userResponse.data.authenticatedId,
      authenticatedId: userResponse.data.authenticatedId,
      email: userResponse.data.email,
      name: userResponse.data.displayName,
      displayName: userResponse.data.displayName,
      avatar: userResponse.data.photo_url,
      photoUrl: userResponse.data.photo_url,
      role: userResponse.data.role || 'AGENT',
      extensionNumber: userResponse.data.extension?.toString(),
      extensionType: userResponse.data.extension_type,
      mobileNumber: userResponse.data.mobileNumber,
      status: userResponse.data.status,
      sipPasswordEncrypted: userResponse.data.sip_password,
    };

    return { user, tokens } as AuthResponse;
  }

  async login(request: LoginRequest): Promise<AuthResponse> {
    // Step 1: Get tokens (refresh token set as cookie by backend)
    const tokenResponse = await apiClient.post<{
      access_token: string;
      expires_in: number;
    }>('/login', {
      email: request.email,
      password: request.password,
    });

    const tokens = {
      accessToken: tokenResponse.data.access_token,
      expiresIn: tokenResponse.data.expires_in,
      tokenType: 'Bearer' as const,
    };

    // Step 2: Fetch user profile
    const userResponse = await apiClient.get<{
      authenticatedId: string;
      displayName: string;
      extension?: string;
      extension_type?: string;
      role?: string;
      photo_url?: string;
      email: string;
      mobileNumber?: string;
      status?: string;
      sip_password?: string;
    }>('/app/me', {
      headers: { Authorization: `Bearer ${tokens.accessToken}` },
    });

    const user = {
      id: userResponse.data.authenticatedId,
      authenticatedId: userResponse.data.authenticatedId,
      email: userResponse.data.email,
      name: userResponse.data.displayName,
      displayName: userResponse.data.displayName,
      avatar: userResponse.data.photo_url,
      photoUrl: userResponse.data.photo_url,
      role: userResponse.data.role || 'AGENT',
      extensionNumber: userResponse.data.extension?.toString(),
      extensionType: userResponse.data.extension_type,
      mobileNumber: userResponse.data.mobileNumber,
      status: userResponse.data.status,
      sipPasswordEncrypted: userResponse.data.sip_password,
    };

    return { user, tokens } as AuthResponse;
  }

  async refreshToken(): Promise<TokenRefreshResponse> {
    const response = await apiClient.post<{
      access_token: string;
      expires_in: number;
    }>('/auth/refresh');

    return {
      tokens: {
        accessToken: response.data.access_token,
        expiresIn: response.data.expires_in,
        tokenType: 'Bearer',
      },
    };
  }

  async logout(_request?: LogoutRequest): Promise<void> {
    await apiClient.post('/app/logout');
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const authDatasource: AuthDatasource = new AuthDatasourceImpl();
